import { ILocalesResponse } from '@joc/api-gateway';

export type SupportedLanguageKey = 'en' | 'fr' | 'he' | 'ru' | 'uk';

export interface LanguageConfig {
	key: SupportedLanguageKey;
	label: string;
	required: boolean;
	dir: 'ltr' | 'rtl';
	defaultLocaleId: number;
}

export const SUPPORTED_LANGUAGES: readonly LanguageConfig[] = [
	{ key: 'en', label: 'English', required: true, dir: 'ltr', defaultLocaleId: 1 },
	{ key: 'fr', label: 'French', required: false, dir: 'ltr', defaultLocaleId: 2 },
	{ key: 'he', label: 'Hebrew', required: false, dir: 'rtl', defaultLocaleId: 3 },
	{ key: 'ru', label: 'Russian', required: false, dir: 'ltr', defaultLocaleId: 4 },
	{ key: 'uk', label: 'Ukrainian', required: false, dir: 'ltr', defaultLocaleId: 5 },
];

export type LocalizedStringsMap = Record<SupportedLanguageKey, string>;

export const createEmptyLocalizedMap = (): LocalizedStringsMap => ({
	en: '',
	fr: '',
	he: '',
	ru: '',
	uk: '',
});

export const matchLanguageKey = (
	locale: Pick<ILocalesResponse, 'localeShortName' | 'localeName'>
): SupportedLanguageKey | undefined => {
	const short = (locale.localeShortName || '').trim().toLowerCase();
	const name = (locale.localeName || '').trim().toLowerCase();

	if (short === 'en' || short.startsWith('en-') || short.startsWith('en_') || name.includes('english')) {
		return 'en';
	}
	if (
		short === 'fr' ||
		short.startsWith('fr-') ||
		short.startsWith('fr_') ||
		name.includes('french') ||
		name.includes('français')
	) {
		return 'fr';
	}
	if (
		short === 'he' ||
		short === 'iw' ||
		short.startsWith('he-') ||
		short.startsWith('he_') ||
		name.includes('hebrew') ||
		name.includes('עברית')
	) {
		return 'he';
	}
	if (
		short === 'ru' ||
		short.startsWith('ru-') ||
		short.startsWith('ru_') ||
		name.includes('russian') ||
		name.includes('русский')
	) {
		return 'ru';
	}
	if (
		short === 'uk' ||
		short === 'ua' ||
		short.startsWith('uk-') ||
		short.startsWith('uk_') ||
		name.includes('ukrainian') ||
		name.includes('українська')
	) {
		return 'uk';
	}

	return undefined;
};

export const buildLocaleIdMap = (locales?: ILocalesResponse[]): Record<SupportedLanguageKey, number> => {
	const map: Record<SupportedLanguageKey, number> = {
		en: SUPPORTED_LANGUAGES[0].defaultLocaleId,
		fr: SUPPORTED_LANGUAGES[1].defaultLocaleId,
		he: SUPPORTED_LANGUAGES[2].defaultLocaleId,
		ru: SUPPORTED_LANGUAGES[3].defaultLocaleId,
		uk: SUPPORTED_LANGUAGES[4].defaultLocaleId,
	};

	if (!locales?.length) {
		return map;
	}

	locales.forEach(locale => {
		const matchedKey = matchLanguageKey(locale);
		if (matchedKey && typeof locale.id === 'number') {
			map[matchedKey] = locale.id;
		}
	});

	return map;
};

export const buildIdToLanguageKeyMap = (
	localeIdMap: Record<SupportedLanguageKey, number>
): Record<number, SupportedLanguageKey> => {
	const reverseMap: Record<number, SupportedLanguageKey> = {};
	SUPPORTED_LANGUAGES.forEach(({ key }) => {
		const id = localeIdMap[key];
		if (typeof id === 'number') {
			reverseMap[id] = key;
		}
	});
	return reverseMap;
};
